import { aperture } from "../../design-system/components/aperture.ts";
import { button } from "../../design-system/components/button.ts";
import { icon } from "../../design-system/components/icon.ts";
import { el, replaceChildren, uniqueId } from "../../design-system/dom.ts";
import {
  formatMoney,
  formatMoneyParts,
  type MoneyDisplayParts,
} from "../../design-system/format/money.ts";
import type { ShellState } from "../state.ts";
import type { Tenant } from "../tenants.ts";
import type { ClaimFlow } from "./claim-experience.ts";
import { type RecipientForm, recipientForm } from "./recipient-form.ts";

/**
 * The claimant surface — what a person receiving money would see.
 *
 * ## The shape of the product, argued in one line
 *
 * A person who has been sent money wants one thing, and every screen here is
 * built around not getting in the way of it: **the exact amount, and one action
 * that names it.** A returning recipient reaches success in a single press. A
 * first-time recipient reaches it in three, and the two extra presses exist only
 * because a destination has to be established by the bank before money can
 * arrive anywhere — not because a review step felt safer to add.
 *
 * There is no review screen, no confirmation dialog and no "are you sure".
 * Receiving money is not a destructive action, and the UI UX Pro Max
 * `confirmation-dialogs` rule reserves confirmation for actions that lose work
 * or cannot be undone. A chain of them here would be friction dressed as care.
 *
 * ## What it is allowed to know
 *
 * Amount, currency, the institution, what that institution requires of a
 * recipient, and whether a receiving destination exists. That is the whole list,
 * and it is the list `CLAUDE.md` § Privacy leaves once claim secrets, customer
 * identifiers, routing references, transfer identifiers and sender identity are
 * removed. There is no sender on this surface at all.
 *
 * ## The one thing it collects, and what happens to it
 *
 * The sending bank decides which recipient details it needs in order to deliver
 * the money, and a first-time recipient supplies them here. Those values are
 * **recipient payout information, not bank credentials** — the distinction
 * `recipient-requirements.ts` is built around — and they live in memory in the
 * form component for as long as the form is on screen. They are never persisted,
 * never transmitted, never logged, never put in the URL, and never copied into
 * the flow, the provider or the destination; the form is cleared on submission
 * and again on teardown.
 *
 * A credential field cannot be configured, cannot be typed, and cannot be
 * rendered: the field vocabulary is closed and the `receiver-claim-boundary`
 * gate fails the build on password, PIN, OTP, biometric and token constructs.
 *
 * ## What it does not contain
 *
 * No URL, no fragment, no storage, no network, no analytics. No engineering
 * control: the scenario dials that drive it live in the reference page outside
 * this surface, and a test asserts that every control inside it is one of the
 * product actions below.
 *
 * ## Hosting neutrality
 *
 * This is a component, not a page. It has no route, reads no location and knows
 * no origin, so mounting it decides nothing about where the eventual production
 * claimant experience lives — which is still open, and is not this milestone's
 * to settle. See `docs/roadmap/PHASE_03_CLAIM_EXPERIENCE_SCOPE.md`.
 */

const LOCALE = "en-GB";

/**
 * Every control this surface may render, as its exact label shape.
 *
 * Exported so `frontend/test/receiver-boundary.test.ts` can assert that nothing
 * else is reachable from the claimant surface — an engineering affordance that
 * arrived here later would have to add itself to this list first, in a diff that
 * says so.
 */
export const CLAIM_SURFACE_ACTIONS = {
  /** Confirms the amount. The one primary action in the product, pressed once. */
  claim: (amount: string): string => `Claim ${amount}`,
  /**
   * Submits the details the sending bank requires.
   *
   * Just "Continue". The bank's name is already on the surface and in the
   * sentence above the form; repeating it in the control would be the third time
   * in one screen, and the amount was confirmed by the press that led here.
   */
  continue: "Continue",
  /** Steps out before anything is in flight. */
  notNow: "Not now",
  /** Recovery after a synthetic failure. Calm, and the word a person expects. */
  tryAgain: "Try again",
  /** Returns to the start after stepping out deliberately. */
  backToClaim: "Back to the claim",
  /** Ends the experience after success. */
  done: "Done",
} as const;

/**
 * How copy names the institution.
 *
 * The tenant's own name for a bank, and **"the sending bank"** for the Nura
 * reference identity — never "Nura". Two separate reasons:
 *
 * - interpolating the tenant name blindly produced sentences in which Nura
 *   authenticates and authorises, inverting the authority `CLAUDE.md`
 *   § Product boundary fixes;
 * - "your bank" is the wrong relationship on a *receiver's* screen. The person
 *   claiming may have no relationship with the institution sending the money,
 *   and telling them it is theirs is both inaccurate and quietly confusing.
 */
function bankLabel(tenant: Tenant): string {
  return tenant.isReference === true ? "the sending bank" : tenant.name;
}

function bankLabelLeading(tenant: Tenant): string {
  const label = bankLabel(tenant);
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

/**
 * How copy names the orchestration layer.
 *
 * `attribution: "none"` means no customer-facing Nura attribution, and body copy
 * is customer-facing. A white-labelled surface that hides the wordmark while
 * sentences still say "Nura" has attributed the vendor anyway.
 */
function serviceLeading(tenant: Tenant): string {
  return tenant.attribution === "none" ? "This service" : "Nura";
}

/**
 * The identity lockup at the top of the surface.
 *
 * The same rule the shell applies, on a surface the shell does not own: with
 * `attribution: "nura"` Nura's wordmark leads, and otherwise the institution
 * leads alone and Nura appears nowhere in the header.
 */
function identityLockup(tenant: Tenant): HTMLElement {
  if (tenant.attribution === "nura") {
    return el("span", { class: "nura-claim-identity" }, [
      el("span", { class: "nura-claim-identity__mark", staticHtml: aperture(16, { weight: 10 }) }),
      el("span", { class: "nura-claim-identity__name", text: tenant.name }),
    ]);
  }

  return el("span", { class: "nura-claim-identity" }, [
    el("span", { class: "nura-claim-identity__initials", text: tenant.initials }),
    el("span", { class: "nura-claim-identity__name", text: tenant.name }),
  ]);
}

/** The footer attribution, following the tenant's commercial setting. */
function attributionLine(tenant: Tenant): HTMLElement | undefined {
  if (tenant.attribution === "powered-by-nura") {
    return el("p", { class: "nura-claim-attribution" }, [
      el("span", {
        class: "nura-claim-attribution__mark",
        staticHtml: aperture(12, { weight: 11 }),
      }),
      el("span", { text: "Powered by Nura" }),
    ]);
  }
  // The reference identity already leads the header, and a fully white-labelled
  // surface carries no Nura wording anywhere.
  return undefined;
}

/**
 * The standing truthful disclosure.
 *
 * Present in every state and in both presentation modes: this screen ends in a
 * success that looks exactly like a completed payment and is not one, and a
 * polished interface that stays quiet about that misleads the person holding the
 * phone as much as the reviewer.
 *
 * **One quiet line, and only one.** The full account of what is and is not
 * implemented belongs on the internal reference page outside this surface, where
 * an engineer is reading; three paragraphs of architecture inside a payment
 * screen is not honesty, it is noise that people learn to skip. What survives is
 * the part a person actually needs: nothing moves, and nothing they type leaves
 * the browser.
 */
function disclosure(): HTMLElement {
  return el("p", { class: "nura-claim-disclosure", attrs: { role: "note" } }, [
    el("span", {
      class: "nura-claim-disclosure__icon",
      staticHtml: icon("shield-check", 14),
    }),
    el("span", { text: "Demo only — no money moves and details are not sent or stored." }),
  ]);
}

/**
 * The headline: an amount that leads, and the words that qualify it.
 *
 * The amount is set the way the sender surface sets it — currency symbol in the
 * brand colour at roughly a third of the size, minor units a step down and
 * muted, tabular figures, tight tracking. Same system, same money, both halves
 * of the product; the digits a person is looking for are the largest thing on
 * the screen and everything else gets out of their way.
 *
 * `aria-label` carries the plain formatted string. Split across three elements
 * the value is correct to look at and wrong to listen to — "€ 100 .00" — so the
 * heading announces the sentence rather than its typography.
 */
function headline(
  id: string,
  amount: string,
  trailing: string,
  parts: MoneyDisplayParts,
): HTMLElement {
  const symbol = el("span", { class: "nura-claim-amount__symbol", text: parts.symbol });
  const digits = [
    el("span", { text: parts.major }),
    parts.minor === ""
      ? undefined
      : el("span", { class: "nura-claim-amount__minor", text: parts.minor }),
  ];

  return el(
    "h1",
    {
      class: "nura-claim-headline",
      attrs: { id, tabindex: "-1", "aria-label": `${amount} ${trailing}`.trim() },
    },
    [
      el(
        "span",
        { class: "nura-claim-amount" },
        parts.symbolLeads ? [symbol, ...digits] : [...digits, symbol],
      ),
      el("span", { class: "nura-claim-headline__text", text: trailing }),
    ],
  );
}

/** The heading currently on screen, whichever shape it took. */
function currentHeading(body: HTMLElement): HTMLElement | undefined {
  const heading = body.querySelector(".nura-claim-headline");
  return heading instanceof HTMLElement ? heading : undefined;
}

/** The headline for states where the amount is not the subject of the sentence. */
function plainHeadline(id: string, text: string): HTMLElement {
  return el("h1", {
    class: "nura-claim-headline nura-claim-headline--plain",
    text,
    attrs: { id, tabindex: "-1" },
  });
}

/**
 * The destination row.
 *
 * A label and a state, never a number. `tone` is paired with an icon and a
 * sentence in both cases, so nothing here depends on colour — the UI UX Pro Max
 * `color-not-only` rule, which matters more when the thing being signalled is
 * where money lands.
 */
function destinationRow(options: {
  readonly tone: "set" | "unset";
  readonly label: string;
  /** Omitted where the label already says everything there is to say. */
  readonly note?: string;
}): HTMLElement {
  return el("div", { class: "nura-claim-destination", data: { tone: options.tone } }, [
    el("span", {
      class: "nura-claim-destination__icon",
      staticHtml: icon(options.tone === "set" ? "check-circle" : "info", 18),
    }),
    el("div", { class: "nura-claim-destination__body" }, [
      el("p", { class: "nura-claim-destination__label", text: options.label }),
      options.note === undefined
        ? undefined
        : el("p", { class: "nura-claim-destination__note", text: options.note }),
    ]),
  ]);
}

function actions(children: readonly HTMLElement[], entering = false): HTMLElement {
  return el(
    "div",
    { class: "nura-claim-actions", data: { enter: entering ? "true" : undefined } },
    children,
  );
}

/**
 * One state's screen, in three parts.
 *
 * The split is a layout contract rather than a convenience. Content is optically
 * centred in the column, the actions sit at the bottom where a thumb is, and a
 * closing note sits below both — in that order, in every state, so the primary
 * action never moves between screens and never has to be hunted for.
 */
interface StateView {
  readonly content: readonly (HTMLElement | undefined)[];
  readonly actions?: readonly HTMLElement[];
  readonly notes?: readonly (HTMLElement | undefined)[];
}

export interface ClaimSurfaceOptions {
  readonly state: ShellState;
  readonly flow: ClaimFlow;
  /**
   * The person is finished with this surface.
   *
   * What that means belongs to whatever hosts the surface, which is exactly the
   * decision this milestone does not take. The reference host closes its
   * overlay; a production host would do something else entirely.
   */
  readonly onFinish?: () => void;
}

export interface ClaimSurface {
  readonly root: HTMLElement;
  /** The id of the heading, for a host that labels a container with it. */
  readonly headingId: string;
  /** Move focus to the current heading. */
  focus(): void;
  destroy(): void;
}

export function claimSurface(options: ClaimSurfaceOptions): ClaimSurface {
  const { state, flow } = options;
  const headingId = uniqueId("nura-claim-heading");

  const identity = el("div", { class: "nura-claim-surface__identity" });
  const body = el("div", { class: "nura-claim-surface__body" });
  const foot = el("div", { class: "nura-claim-surface__foot" });

  const root = el("section", { class: "nura-claim-surface" }, [identity, body, foot]);

  /**
   * The state the current DOM was built for.
   *
   * Focus moves only when this changes, so a re-render that changes nothing else
   * does not announce the same heading twice.
   */
  let renderedState: string | undefined;

  /**
   * The state an asynchronous action was started from, while it is in flight.
   *
   * Not a plain boolean, and the difference matters. A spinner belongs on the
   * button that was pressed, which only exists on the screen it was pressed on;
   * once the flow has moved to `processing` the press is over as far as the
   * interface is concerned. Recording the *state* rather than a flag makes the
   * busy indicator local to that screen — and stops the flag's own clearing from
   * rebuilding the next screen, which silently threw focus back to the document
   * every time an action settled.
   */
  let busyFrom: string | undefined;

  /** True while an action started from the state currently on screen is running. */
  function busy(): boolean {
    return busyFrom === flow.state;
  }

  /**
   * The recipient form, for as long as the screen that owns it is on screen.
   *
   * Memoised rather than rebuilt on every render, for one non-cosmetic reason:
   * a render happens when the Continue button becomes busy, and rebuilding the
   * form there would blank the fields a person had just filled in while their
   * submission was in flight. It is discarded — and emptied — the moment the
   * flow leaves this state or the tenant changes underneath it, so entered
   * values never outlive the screen that asked for them.
   */
  let form: RecipientForm | undefined;
  let formTenantId: string | undefined;

  function activeForm(): RecipientForm {
    if (form === undefined || formTenantId !== state.tenant.id) {
      form?.clear();
      form = recipientForm({ requirements: flow.requirements });
      formTenantId = state.tenant.id;
    }
    return form;
  }

  /** Empty and drop the form. Called on every transition out of its state. */
  function releaseForm(): void {
    form?.clear();
    form = undefined;
    formTenantId = undefined;
  }

  /**
   * What the current DOM depends on.
   *
   * Rendering is skipped when this is unchanged, so an update that changes
   * nothing a person can see does not destroy and rebuild the element they are
   * focused on.
   */
  function signature(): string {
    return [
      flow.state,
      String(busy()),
      state.tenant.id,
      state.presentation,
      flow.destination?.label ?? "",
    ].join("|");
  }

  let rendered: string | undefined;

  function amountText(): string {
    const { amountMinor, currency } = flow.presentation;
    return formatMoney(amountMinor, currency, LOCALE);
  }

  function amountParts(): MoneyDisplayParts {
    const { amountMinor, currency } = flow.presentation;
    return formatMoneyParts(amountMinor, currency, LOCALE);
  }

  function claimButton(): HTMLButtonElement {
    return button({
      label: CLAIM_SURFACE_ACTIONS.claim(amountText()),
      variant: "primary",
      block: true,
      loading: busy(),
      onClick: () => {
        void run(() => flow.claim());
      },
    });
  }

  /**
   * Run one asynchronous product action.
   *
   * The button is disabled and marked busy for the duration, so the double press
   * that would start a second claim is not reachable from the interface —
   * `CLAUDE.md` § Transaction integrity, and the UI UX Pro Max `loading-buttons`
   * rule, which exist for the same reason.
   */
  async function run(action: () => Promise<void>): Promise<void> {
    if (busyFrom !== undefined) return;
    busyFrom = flow.state;
    render();
    try {
      await action();
    } finally {
      busyFrom = undefined;
      render();
    }
  }

  function readyBody(): StateView {
    const tenant = state.tenant;
    const destination = flow.destination;

    return {
      content: [
        headline(headingId, amountText(), "is waiting for you", amountParts()),
        destination === undefined
          ? destinationRow({
              tone: "unset",
              label: "Where you receive money",
              note: `${bankLabelLeading(tenant)} asks for a few details when you claim.`,
            })
          : // The label is the whole status. A second line repeating it under
            // a check mark is the interface talking to itself.
            destinationRow({ tone: "set", label: destination.label }),
      ],
      actions: [claimButton()],
      notes: [
        el("p", {
          class: "nura-claim-note",
          /*
           * The precise claim, and no more than it.
           *
           * "Does not store" is what the architecture actually guarantees:
           * recipient details are not persisted in Nura-operated infrastructure.
           * "Never sees" would have been a stronger promise than the code can
           * keep — Nura *client* code renders the form in this build, and may
           * transiently handle a value under a direct-to-bank submission model.
           * "Receiving details" rather than "account details", because the
           * destination may be an IBAN, an account number or a phone number.
           */
          text: `${serviceLeading(tenant)} does not store your receiving details.`,
        }),
      ],
    };
  }

  function recipientDetailsBody(): StateView {
    const tenant = state.tenant;
    const active = activeForm();

    return {
      content: [
        el("p", { class: "nura-claim-eyebrow", text: `Claiming ${amountText()}` }),
        plainHeadline(
          headingId,
          `${bankLabelLeading(tenant)} needs a few details to send your money`,
        ),
        el("p", {
          class: "nura-claim-lede",
          // What is being asked for, and by whom — on the screen where a person
          // decides whether to type. Nothing about custody mechanics beyond the
          // one boundary they have a stake in.
          text: `${bankLabelLeading(tenant)} decides what information it needs to send your money. ${serviceLeading(tenant)} does not store your receiving details.`,
        }),
        active.root,
      ],
      actions: [
        button({
          label: CLAIM_SURFACE_ACTIONS.continue,
          variant: "primary",
          block: true,
          loading: busy(),
          onClick: () => {
            if (!active.validate()) return;
            // The values leave the form once, as an argument, and the form is
            // emptied in the same breath.
            const values = active.values();
            active.clear();
            void run(() => flow.submitRecipientDetails({ values }));
          },
        }),
        button({
          label: CLAIM_SURFACE_ACTIONS.notNow,
          variant: "ghost",
          block: true,
          disabled: busy(),
          onClick: () => {
            flow.cancel();
          },
        }),
      ],
    };
  }

  function confirmingDetailsBody(): StateView {
    const tenant = state.tenant;

    return {
      content: [
        el("div", { class: "nura-claim-progress", attrs: { "aria-hidden": true } }, [
          el("span", {
            class: "nura-claim-progress__ring",
            staticHtml: aperture(48, { weight: 7 }),
          }),
        ]),
        el("p", { class: "nura-claim-eyebrow", text: `Claiming ${amountText()}` }),
        plainHeadline(
          headingId,
          `${bankLabelLeading(tenant)} is confirming your receiving details`,
        ),
      ],
    };
  }

  function processingBody(): StateView {
    const tenant = state.tenant;

    return {
      content: [
        el("div", { class: "nura-claim-progress", attrs: { "aria-hidden": true } }, [
          el("span", {
            class: "nura-claim-progress__ring",
            staticHtml: aperture(48, { weight: 7 }),
          }),
        ]),
        headline(headingId, amountText(), "is being claimed", amountParts()),
        el("p", {
          class: "nura-claim-lede",
          text: `${bankLabelLeading(tenant)} is completing this.`,
        }),
      ],
    };
  }

  function successBody(): StateView {
    const destination = flow.destination;

    return {
      content: [
        el("div", { class: "nura-claim-outcome", attrs: { "aria-hidden": true } }, [
          el("span", {
            class: "nura-claim-outcome__ring",
            staticHtml: aperture(56, { state: "closed", weight: 7, draw: true }),
          }),
          el("span", { class: "nura-claim-outcome__glyph", staticHtml: icon("check", 22) }),
        ]),
        /*
         * The whole message.
         *
         * Not "claimed", which is the system's word for what just happened to a
         * record; "on the way", which is the person's word for what happened to
         * their money. Nothing about which institution completes it, how it
         * settles, or where it routes — none of that is the recipient's problem,
         * and a success screen that explains mechanics reads as a system
         * reassuring itself.
         */
        headline(headingId, amountText(), "is on the way!", amountParts()),
        destination === undefined
          ? undefined
          : destinationRow({ tone: "set", label: destination.label }),
      ],
      actions: [
        button({
          label: CLAIM_SURFACE_ACTIONS.done,
          variant: "secondary",
          block: true,
          onClick: () => {
            options.onFinish?.();
          },
        }),
      ],
    };
  }

  function unavailableBody(): StateView {
    const tenant = state.tenant;

    return {
      content: [
        el("div", { class: "nura-claim-outcome", data: { tone: "attention" } }, [
          el("span", { class: "nura-claim-outcome__glyph", staticHtml: icon("warning", 22) }),
        ]),
        // Plain, unalarmed, and free of any reason — because the reason belongs
        // to the bank and this build has none. What a person needs from this
        // screen is that their money is safe and that they can try again.
        plainHeadline(headingId, "That didn't go through"),
        el("p", {
          class: "nura-claim-lede",
          text: `Nothing was claimed. ${amountText()} is still waiting for you.`,
        }),
      ],
      actions: [
        button({
          label: CLAIM_SURFACE_ACTIONS.tryAgain,
          variant: "primary",
          block: true,
          onClick: () => {
            flow.resume();
          },
        }),
      ],
    };
  }

  function cancelledBody(): StateView {
    return {
      content: [
        el("div", { class: "nura-claim-outcome", data: { tone: "neutral" } }, [
          el("span", { class: "nura-claim-outcome__glyph", staticHtml: icon("prohibit", 24) }),
        ]),
        plainHeadline(headingId, "Nothing was claimed"),
        el("p", {
          class: "nura-claim-lede",
          text: `${amountText()} is still waiting for you.`,
        }),
      ],
      actions: [
        button({
          label: CLAIM_SURFACE_ACTIONS.backToClaim,
          variant: "primary",
          block: true,
          onClick: () => {
            flow.resume();
          },
        }),
      ],
    };
  }

  function bodyForState(): StateView {
    switch (flow.state) {
      case "ready":
        return readyBody();
      case "recipientDetailsRequired":
        return recipientDetailsBody();
      case "confirmingDetails":
        return confirmingDetailsBody();
      case "processing":
        return processingBody();
      case "success":
        return successBody();
      case "unavailable":
        return unavailableBody();
      case "cancelled":
        return cancelledBody();
    }
  }

  function render(): void {
    const next = signature();
    if (next === rendered) return;
    rendered = next;

    // Leaving the form's screen destroys the form and everything typed into it.
    if (flow.state !== "recipientDetailsRequired") releaseForm();

    /*
     * Whether this render is a new screen or the same screen redrawn.
     *
     * Only a new screen animates in. A redraw — the Continue button becoming
     * busy, a theme change — would otherwise replay a fade over content the
     * person is already reading, which reads as a flicker rather than as a
     * transition.
     */
    const entering = renderedState !== undefined && renderedState !== flow.state;

    root.dataset["state"] = flow.state;
    replaceChildren(identity, [identityLockup(state.tenant)]);

    const view = bodyForState();
    replaceChildren(body, [
      el(
        "div",
        { class: "nura-claim-content", data: { enter: entering ? "true" : undefined } },
        view.content,
      ),
      view.actions === undefined ? undefined : actions(view.actions, entering),
      ...(view.notes ?? []),
    ]);

    replaceChildren(foot, [disclosure(), attributionLine(state.tenant)]);

    // Every state replaces the whole body, so the control that was focused is
    // gone. Left alone, focus falls to the document and a keyboard or screen
    // reader user loses their place mid-payment. Moving it to the new heading is
    // what a full page load would have done, and is what the router does.
    renderedState = flow.state;
    if (entering) focus();
  }

  function focus(): void {
    currentHeading(body)?.focus({ preventScroll: true });
  }

  const unsubscribeFlow = flow.subscribe(render);
  const unsubscribeShell = state.subscribe(render);
  render();

  return {
    root,
    headingId,
    focus,
    destroy(): void {
      // Closing the surface discards anything entered into it, whether or not
      // the flow reached a terminal state.
      releaseForm();
      unsubscribeFlow();
      unsubscribeShell();
    },
  };
}
